import styled from 'styled-components';
import { DeployContractPackageResult } from '@block-explorer/entities';
import { useTranslation } from 'react-i18next';
import { buildRouterPath } from '../../../router/utils';
import { CONTRACT_PACKAGE_PATH } from '../../../router/paths';
import {
  SHORTENED_CONTRACT_NAME_LENGTH,
  truncateName,
} from '../../../components/action-messages/utils';
import ContractIcon from '../../../components/contract-icon/contract-icon';
import { BodyText, HashLength, Tooltip, Link } from '@block-explorer/ui';
import { HashLink } from '../../../components';
import React from 'react';
import {
  FlexColumn,
  FlexRow,
  SubtitleText,
  SvgIcon,
  TableData,
  TableRow,
} from '@make-software/cspr-design';

const StyledTableRow = styled(TableRow)(({ theme }) =>
  theme.withMedia({
    '&&': {
      ':hover, :active': {
        background: 'inherit',
      },
      ':last-child:after': {
        borderBottom: 0,
      },
    },
  })
);
const StyledTableData = styled(TableData)(({ theme }) =>
  theme.withMedia({
    '&&': {
      padding: '16px 0 0',
      width: '100%',
    },
  })
);

const StyledSvgIcon = styled(SvgIcon)(({ theme }) => ({
  path: {
    fill: theme.styleguideColors.contentLightBlue,
  },
}));

interface FeaturedContractsRowProps {
  featuredContract: DeployContractPackageResult;
}

export const FeaturedContractsRow = ({
  featuredContract,
}: FeaturedContractsRowProps) => {
  const { t } = useTranslation();

  const {
    latest_version_contract_type_id,
    contract_package_hash,
    name,
    website_url,
    metadata,
    icon_url,
    description,
  } = featuredContract;

  const path = buildRouterPath(
    CONTRACT_PACKAGE_PATH,
    { id: contract_package_hash },
    undefined
  );

  const shortenedWebsite = truncateName(website_url, 28);
  const shortenName = truncateName(name, SHORTENED_CONTRACT_NAME_LENGTH);

  return (
    <StyledTableRow>
      <StyledTableData fitContent>
        <FlexColumn itemsSpacing={32}>
          <FlexRow align="center" itemsSpacing={16}>
            <ContractIcon
              size={'medium'}
              contractTypeId={latest_version_contract_type_id}
              src={icon_url || metadata?.contract_icon_uri}
              loading={false}
            />
            <FlexColumn itemsSpacing={2}>
              {name ? (
                <Tooltip caption={t('Contract Name')} tooltipContent={name}>
                  <SubtitleText size={1} scale={'md'} variation="black" noWrap>
                    {shortenName}
                  </SubtitleText>
                </Tooltip>
              ) : null}
              <Tooltip
                caption={t('Contract package hash')}
                tooltipContent={contract_package_hash}
              >
                <BodyText size={3} scale="sm" lineHeight={'xs'} monotype>
                  <HashLink
                    href={path}
                    hash={contract_package_hash}
                    hashLength={HashLength.TINY}
                    ariaDescription={`Link to Contract Package page`}
                  />
                </BodyText>
              </Tooltip>
              {website_url ? (
                <Link color="primaryBlue" href={website_url} rel="noopener">
                  <Tooltip caption={t('Website')} tooltipContent={website_url}>
                    <FlexRow align={'center'} gap={8}>
                      <BodyText size={3} scale={'sm'}>
                        {shortenedWebsite}
                      </BodyText>
                      <StyledSvgIcon
                        src="assets/icons/ic-website-link.svg"
                        size={14}
                        role={'img'}
                      />
                    </FlexRow>
                  </Tooltip>
                </Link>
              ) : null}
            </FlexColumn>
          </FlexRow>

          {description || metadata?.contract_description ? (
            <FlexRow itemsSpacing={6} align={'center'}>
              <BodyText size={3} scale="sm" wordBreak>
                {description || metadata?.contract_description}
              </BodyText>
            </FlexRow>
          ) : null}
        </FlexColumn>
      </StyledTableData>
    </StyledTableRow>
  );
};
